import { Controller } from "@hotwired/stimulus";
import { Loader } from "@googlemaps/js-api-loader";
import { Modal } from "bootstrap";

export default class extends Controller {
  static values = {
    apiKey: String,
    markers: Array,
    markerIcon: String,
    mapId: String,
  };

  async connect() {
    this.loader = new Loader({
      apiKey: this.apiKeyValue,
      version: "weekly",
      libraries: ["maps", "marker"],
    });

    await this.loader.importLibrary("maps");
    await this.loader.importLibrary("marker");
    const google = globalThis.google;

    const mapOptions = {
      mapId: this.hasMapIdValue ? this.mapIdValue.trim() : undefined,
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      zoomControl: true,
      clickableIcons: false,
    };

    const initialMarkers = this.readMarkers();
    const markers = initialMarkers.filter(
      (m) => isFinite(m.lat) && isFinite(m.lng),
    );

    this.map = new google.maps.Map(this.element, mapOptions);

    if (markers.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
      this.map.fitBounds(bounds);
    } else {
      const bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(48.066334, -130.012207),
        new google.maps.LatLng(52.498835, -115.158691),
      );
      this.map.fitBounds(bounds);
    }

    this.google = google;
    this.useAdvancedMarkers = !!(
      mapOptions.mapId && google?.maps?.marker?.AdvancedMarkerElement
    );

    if (!this.useAdvancedMarkers) {
      console.warn(
        "AdvancedMarkerElement unavailable (missing mapId or library); falling back to google.maps.Marker.",
      );
    }
    this.infoWindow = new google.maps.InfoWindow();
    this.markerIcon = this.buildMarkerIcon();
    this.renderMarkers(markers);
  }

  disconnect() {
    this.infoWindow = null;
    this.map = null;
    this.loader = null;
    this.google = null;
  }

  renderMarkers(markers) {
    if (this.markers && this.markers.length) {
      this.markers.forEach((mk) => (mk.map = null));
    }

    this.markers = markers.map((m) => {
      let marker;
      if (this.useAdvancedMarkers) {
        marker = new this.google.maps.marker.AdvancedMarkerElement({
          position: { lat: m.lat, lng: m.lng },
          map: this.map,
          title: m.name,
          gmpClickable: true,
          content: this.markerIcon
            ? this.markerIcon.cloneNode(true)
            : undefined,
        });
      } else {
        marker = new this.google.maps.Marker({
          position: { lat: m.lat, lng: m.lng },
          map: this.map,
          title: m.name,
          icon: this.markerIcon,
        });
      }

      marker.addListener("click", () => {
        this.renderInfoWindow(m, marker);
      });
      return marker;
    });
  }

  renderInfoWindow(m, marker) {
    if (!this.modalId) this.modalId = `google-map-modal-${m.id}`;

    let modalEl = this.modalEl || document.getElementById(this.modalId);
    if (!modalEl) {
      modalEl = document.createElement("div");
      modalEl.className = "modal fade";
      modalEl.id = this.modalId;
      modalEl.tabIndex = -1;
      modalEl.setAttribute("aria-hidden", "true");
      modalEl.innerHTML = `
          <div class="modal-dialog modal-dialog-scrollable modal-md">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title"></h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body"></div>
            </div>
          </div>`;
      document.body.appendChild(modalEl);
      this.modalEl = modalEl;
    }

    const titleEl = modalEl.querySelector(".modal-title");
    const bodyEl = modalEl.querySelector(".modal-body");
    titleEl.textContent = m.name || "";
    bodyEl.textContent = "";

    if (m.description) {
      const section = document.createElement("div");
      section.className = "mb-3";
      const inner = document.createElement("div");
      inner.className = "lh-sm";
      inner.style.whiteSpace = "pre-wrap";
      inner.textContent = m.description;
      section.appendChild(inner);
      bodyEl.appendChild(section);
    }

    const detailItems = this.#buildDetailItems(m);
    if (detailItems.length) {
      const section = document.createElement("div");
      section.className = "mb-3";
      const heading = document.createElement("div");
      heading.className = "fw-semibold bg-light-blue rounded px-2 py-1 mb-2";
      heading.textContent = "Details";
      section.appendChild(heading);
      const ul = document.createElement("ul");
      ul.className = "list-unstyled lh-sm mb-0";
      detailItems.forEach((item) => {
        const li = document.createElement("li");
        li.className = "mb-1";
        li.appendChild(item);
        ul.appendChild(li);
      });
      section.appendChild(ul);
      bodyEl.appendChild(section);
    }

    if (m.community_affiliation) {
      const section = document.createElement("div");
      section.className = "mb-3";
      const heading = document.createElement("div");
      heading.className = "fw-semibold bg-light-blue rounded px-2 py-1 mb-2";
      heading.textContent = "Community Affiliation";
      section.appendChild(heading);
      const content = document.createElement("div");
      content.textContent = m.community_affiliation;
      section.appendChild(content);
      bodyEl.appendChild(section);
    }

    if (Array.isArray(m.categories) && m.categories.length) {
      const heading = document.createElement("div");
      heading.className = "fw-semibold bg-light-blue rounded px-2 py-1 mb-2";
      heading.textContent = "Categories";
      bodyEl.appendChild(heading);
      const badges = document.createElement("div");
      badges.className = "category-badges d-flex flex-column gap-1 flex-wrap";
      m.categories.forEach((c) => {
        const badge = document.createElement("div");
        badge.className = "category-badge";
        const icon = document.createElement("div");
        const iconClass = c.nu_icon || c.icon || "fas fa-tag";
        if (/^[a-z\s-]+$/i.test(iconClass)) {
          icon.className = `me-2 ${iconClass}`;
        } else {
          icon.className = "me-2 fas fa-tag";
        }
        badge.appendChild(icon);
        badge.appendChild(document.createTextNode(c.nu_name || c.name || ""));
        badges.appendChild(badge);
      });
      bodyEl.appendChild(badges);
    }

    if (!this.bsModal) {
      this.bsModal = new Modal(modalEl, {
        backdrop: true,
        keyboard: true,
      });
    }
    this.bsModal.show();
  }

  #buildDetailItems(m) {
    const items = [];

    if (m.address) {
      const span = document.createElement("span");
      span.textContent = m.address;
      items.push(span);
    }
    if (m.contact_name) {
      const span = document.createElement("span");
      span.textContent = m.contact_name;
      items.push(span);
    }
    if (m.phone) {
      const a = document.createElement("a");
      a.href = `tel:${m.phone}`;
      a.textContent = m.phone;
      items.push(a);
    }
    if (m.website) {
      const a = document.createElement("a");
      a.href = m.website;
      a.textContent = m.website;
      a.target = "_blank";
      a.rel = "noopener";
      items.push(a);
    }
    if (m.email) {
      const a = document.createElement("a");
      a.href = `mailto:${m.email}`;
      a.textContent = m.email;
      items.push(a);
    }

    return items;
  }

  readMarkers() {
    if (this.hasMarkersValue) return this.markersValue || [];
    return [];
  }

  buildMarkerIcon() {
    if (!this.google) return null;

    const width = 20;
    const height = 30;

    if (this.useAdvancedMarkers) {
      const wrapper = document.createElement("div");
      wrapper.className = "google-map-marker-icon";
      wrapper.style.width = `${width}px`;
      wrapper.style.height = `${height}px`;
      const img = document.createElement("img");
      img.src = this.markerIconValue;
      img.width = width;
      img.height = height;
      img.alt = "Marker";
      wrapper.appendChild(img);
      return wrapper;
    }

    return {
      url: this.markerIconValue,
      scaledSize: new this.google.maps.Size(width, height),
      anchor: new this.google.maps.Point(Math.round(width / 2), height),
    };
  }
}
